<template>
  <PageTemplate>
    <Loader v-if="!isLoaded"/>
    <div v-else>
      <BackButton
          :label="$t('general.button.back')"
          :to="{ name: 'analyticsHome' }"
          marginBottom="5"/>

      <div class="d-flex justify-content-between">
        <h6 class="element-header">
          Absolvierte Einheiten durch externe Trainer
        </h6>
      </div>

      <div class="element-box">
        <h5 class="form-header mb-4">Einheiten pro Woche</h5>

        <Menu/>

        <Search
            :filters="filters"
            :loading="isSearching"
            :yearDropdown="yearDropdown"
            :monthDropdown="enums.monthsInYear"
            :coachDropdown="coachDropdown"
            @search="getUnitsList"/>

        <div class="row align-items-center justify-content-between">
          <div class="mt-3">
            <PerPage :values="[10, 25, 50, 100]" @update:perPage="getUnitsList"/>

            <ExcelExportButton
                style="margin-right: 15px;"
                :label="$t('general.excel_export')"
                :loading="isExcelLoaded"
                @click="getExcelExport"/>
          </div>
        </div>

        <div class="table-responsive overflow-auto pt-4 mb-3">
          <table class="table table-striped">
            <thead>
            <tr>
              <th>Mitarbeiternr</th>
              <th>Extern/Intern</th>
              <th>Vorname</th>
              <th>Nachname</th>
              <th>Rechte</th>
              <th class="text-end">Pro Monat erledigte Einheiten</th>
              <th class="text-end">Einheiten für das Jahr erledigt</th>
              <th class="text-end">Durchschnittlich geleistete Einheiten pro Jahr</th>
            </tr>
            </thead>
            <tbody>
            <tr v-for="listData in unitsList.data" :key="listData.coachId">
              <td>{{ listData.employeeNumber }}</td>
              <td>{{ $t(`user_engagement_type.${listData.engagementType}`) }}</td>
              <td>{{ listData.firstName }}</td>
              <td>{{ listData.lastName }}</td>
              <td>{{ $t(`user_access_right.${listData.userAccessRights}`) }}</td>
              <td class="text-end">{{ listData.sumUnitsForMonth }}</td>
              <td class="text-end">{{ listData.sumUnitsForYear }}</td>
              <td class="text-end">
                <span style="width: 30px!important; display: inline-block;">
                  <div :class="['status-pill', getStatusClass(listData.sumUnitsForYear), 'p-2', 'me-2']"
                      :title="getStatusTitle(listData.sumUnitsForYear)"></div>
                </span>
                <span style="width: 50px!important; display: inline-block;">
                  {{ formatAveragePerYear(listData.sumUnitsForYear) }}
                </span>
              </td>
            </tr>
            </tbody>
          </table>
        </div>
        <Pagination
            v-if="unitsList?.pagination?.total_pages > 1"
            :totalPages="unitsList?.pagination?.total_pages"
            :currentPage="unitsList?.pagination?.current_page"
            :totalItems="unitsList?.pagination?.total_items"
            :perPage="unitsList?.pagination?.items_per_page"
            @pagechanged="getUnitsList"/>
      </div>
    </div>
  </PageTemplate>
</template>

<script setup>
import analyticsService from '@/api/Analytics';
import Loader from '@/components/Loader.vue';
import PageTemplate from '@/components/PageTemplate.vue';
import Pagination from '@/components/Pagination.vue';
import PerPage from '@/components/PerPage.vue';
import { CloseToast, ErrorToast } from '@/components/VueToast';
import { getExternalCoachDropdown, getYears } from '@/shared/dropdowns';
import { downloadFile } from '@/shared/functions';
import { piniaEnumsStore } from '@/store/piniaEnumsStore';
import Menu from '@/views/App/Analytics/UnitsDoneByExternalCoaches/Menu.vue';
import Search from '@/views/App/Analytics/UnitsDoneByExternalCoaches/Month/Search.vue';
import { computed, onMounted, ref } from 'vue';
import ExcelExportButton from "@/components/ExcelExportButton.vue";
import BackButton from "@/components/button/BackButton.vue";

const { enums } = piniaEnumsStore();
const currentDate = new Date();

const filters = ref({
  coachId: '',
  year: currentDate.getFullYear(),  // Current year as a string
  month: currentDate.getMonth() + 1   // `getMonth()` is zero-based, so add 1
});

const unitsList     = ref([]);
const coachDropdown = ref([]);
const yearDropdown  = ref([]);
const isSearching   = ref(false);
const isLoaded      = ref(false);
const isExcelLoaded = ref(false);

async function getUnitsList(page = 1, perPage, passedFilters) {
  CloseToast();

  if (passedFilters) {
    isSearching.value = true;
  }
  const perPageRes = unitsList.value?.pagination?.items_per_page;

  try {
    const { data } = await analyticsService.getUnitsDoneByExternalCoachesPerMonth({
      page: page,
      limit: perPage ?? perPageRes,
      ...(passedFilters ? passedFilters : filters.value),
    });

    unitsList.value   = data;
    isSearching.value = false;
  } catch (error) {
    await ErrorToast(error);
    isSearching.value = false;
  }
}

const formatAveragePerYear = (sumUnitsForYear) =>
    (sumUnitsForYear / 52).toFixed(2).replace('.', ',');

const getStatusClass = (sumUnitsForYear) => {
  const averagePerYear = sumUnitsForYear / 52;
  if (averagePerYear < 22) return 'green';
  if (averagePerYear > 22 && averagePerYear < 27) return 'yellow';
  return 'red';
};

const getStatusTitle = (sumUnitsForYear) => {
  const averagePerYear = sumUnitsForYear / 52;
  if (averagePerYear < 22) return 'Low average';
  if (averagePerYear > 22 && averagePerYear < 27) return 'Medium average';
  return 'High average';
};

async function getExcelExport() {
  await downloadFile('/analytics/units-done-by-external-coaches/month-excel-export',
      undefined,
      {
        ...filters.value,
      },
      false,
      'GET',
      isExcelLoaded
  )
}

onMounted(async () => {
  await Promise.all([
    getYears(yearDropdown),
    getExternalCoachDropdown(coachDropdown),
    getUnitsList(),
  ]);

  isLoaded.value = true;
});
</script>

<style scoped>

</style>